// Client-only persistence for the Daily Color Pulse mood tracker — no
// accounts, no backend. History lives in the visitor's browser via
// localStorage and never leaves the device.

const STORAGE_KEY = "chromamind:mood-log";

/** Local calendar date key (not UTC) — mood is a personal daily check-in, so
 *  it should follow the visitor's own day boundary, not a server's. */
function dateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function readLog(): Record<string, string> {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeLog(log: Record<string, string>): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(log));
  } catch {
    // Storage unavailable (private browsing, quota exceeded) — the picker
    // still works for the current session, it just won't persist. Fail silent
    // rather than break the interaction.
  }
}

export function getMoodOn(date: Date): string | null {
  return readLog()[dateKey(date)] ?? null;
}

export function getTodayMoodId(): string | null {
  return getMoodOn(new Date());
}

export function setTodayMood(moodId: string): void {
  const log = readLog();
  log[dateKey(new Date())] = moodId;
  writeLog(log);
}

/** Consecutive days (ending today) with a logged mood. 0 if today has none. */
export function getStreak(): number {
  const log = readLog();
  const cursor = new Date();
  if (!log[dateKey(cursor)]) return 0;
  let streak = 0;
  while (log[dateKey(cursor)]) {
    streak += 1;
    cursor.setDate(cursor.getDate() - 1);
  }
  return streak;
}

/** Last `days` calendar days (oldest first, today last) with each day's
 *  logged mood id (or null if none was recorded). */
export function getRecentHistory(days: number): { date: Date; moodId: string | null }[] {
  const log = readLog();
  const out: { date: Date; moodId: string | null }[] = [];
  const cursor = new Date();
  cursor.setDate(cursor.getDate() - (days - 1));
  for (let i = 0; i < days; i++) {
    const d = new Date(cursor);
    out.push({ date: d, moodId: log[dateKey(d)] ?? null });
    cursor.setDate(cursor.getDate() + 1);
  }
  return out;
}
